import { useEffect, useMemo } from 'react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useAppScopeStore } from '@frontend/scope';
import { createStoreWithPersist, createShallowStore } from '@frontend/store';
import { MAX_CALENDAR_LOCATION_COUNT, SCHEDULE_LOCAL_STORAGE_KEYS } from '../constants';
import { useIsCalendarPageOpen } from '../hooks/use-is-calendar-page-open';
import { useSchedulingLocationInfo } from '../hooks/use-scheduling-location-info';
import { sortByTimezone } from '../utils/date-helpers';
import { useCalendarStore } from './calendar.store';

type State = {
  calendarLocationMap: Record<string, string[]>; // object mapping locationGroupId to an array of selected calendar location IDs
};

type Actions = {
  setCalendarLocationMap: (locationGroupId: string, locationIds: string[]) => void;
  clearCalendarLocationMap: () => void;
};

const initialState: State = {
  calendarLocationMap: {},
};

type CalendarLocationStore = State & Actions;

const useStore = createStoreWithPersist<CalendarLocationStore>(
  (set) => ({
    ...initialState,
    setCalendarLocationMap: (locationGroupId: string, locationIds: string[]) => {
      if (!locationGroupId) return;

      set(
        (state) => ({ calendarLocationMap: { ...state.calendarLocationMap, [locationGroupId]: locationIds } }),
        false,
        'SET_CALENDAR_LOCATION_MAP'
      );
    },
    clearCalendarLocationMap: () => {
      set({ calendarLocationMap: {} }, false, 'CLEAR_CALENDAR_LOCATION_MAP');
    },
  }),
  {
    name: SCHEDULE_LOCAL_STORAGE_KEYS.SCHEDULE_CALENDAR_LOCATION_STORE,
  },
  {
    name: 'CalendarLocationStore',
    enabled: true,
    trace: true,
  }
);

export const useCalendarLocationShallowStore = createShallowStore(useStore);

export const useCalendarLocationIds = () => {
  const { selectedGroupId, selectedLocationIds } = useAppScopeStore();
  const isCalendarPageOpen = useIsCalendarPageOpen();
  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');
  const { calendarLocationMap } = useCalendarLocationShallowStore('calendarLocationMap');

  const calendarLocationIds = useMemo(() => {
    const locationIds = calendarLocationMap[selectedGroupId] || [];
    // sort locations by timezone
    const locationTimezones = locationIds.map((locationId) => ({
      locationId,
      timezone: locationTimezoneMap[locationId]?.timezone,
    }));
    const sortedLocationTimezones = sortByTimezone(locationTimezones, 'timezone');
    return sortedLocationTimezones.map((item) => item.locationId);
  }, [calendarLocationMap, locationTimezoneMap, selectedGroupId]);

  // this is used for new out of office and appointment global events
  // where we need to use locations of calendar page only if calendar page is open currently
  const calendarPageLocationIds = isCalendarPageOpen ? calendarLocationIds : selectedLocationIds;

  return { calendarLocationIds, calendarPageLocationIds };
};

// use by calendar v2 & v3 page to update selected location based on global location change
export const useSyncCalendarLocation = () => {
  const { selectedGroupId, selectedLocationIds } = useSchedulingLocationInfo();
  const { resetCalendarFilters } = useCalendarStore(['resetCalendarFilters']);

  useEffect(() => {
    if (!selectedGroupId) return;

    let newLocationIds: string[] = [];
    const calendarLocationIds = useStore.getState().calendarLocationMap[selectedGroupId] || [];

    if (selectedLocationIds.length <= MAX_CALENDAR_LOCATION_COUNT) {
      newLocationIds = [...selectedLocationIds];
    } else if (calendarLocationIds.length > 0) {
      newLocationIds = calendarLocationIds.filter((id) => selectedLocationIds.includes(id));
    }

    useStore.getState().setCalendarLocationMap(selectedGroupId, newLocationIds);
    resetCalendarFilters(newLocationIds);
  }, [resetCalendarFilters, selectedGroupId, selectedLocationIds]);
};

export const resetCalendarLocationStoreOnLogin = () => {
  useStore.getState().clearCalendarLocationMap();
};
